import type { ReactNode } from 'react';
import { ArrowLeft, Box, CreditCard, LayoutDashboard, LogOut, RefreshCw, ServerCog, ShieldAlert, ShieldX, UserX, Users } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import AppLayout from '../AppLayout';
import AppNav, { AppBrand } from '../AppNav';
import { API_CONFIG } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../store/authStore';

interface AdminShellProps {
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}

const navItems = [
  { to: ROUTES.OVERVIEW, label: 'Overview', icon: LayoutDashboard },
  { to: ROUTES.QUEUES, label: 'Queues', icon: ServerCog },
  { to: ROUTES.USERS, label: 'Users', icon: Users },
  { to: ROUTES.PRODUCTS, label: 'Products', icon: Box },
  { to: ROUTES.SUBSCRIPTIONS, label: 'Subscriptions', icon: CreditCard },
  { to: ROUTES.SECURITY, label: 'Security', icon: ShieldAlert },
  { to: ROUTES.COOLDOWN_ACCOUNTS, label: 'Cooldowns', icon: UserX },
  { to: ROUTES.IP_BLOCKS, label: 'IP Blocks', icon: ShieldX },
];

export default function AdminShell({ title, description, actions, children }: AdminShellProps) {
  const { logout } = useAuth();

  return (
    <AppLayout>
      <AppNav
        left={(
          <>
            <button
              onClick={() => window.location.assign(API_CONFIG.USER_FRONTEND_URL)}
              className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> User App
            </button>
            <AppBrand />
          </>
        )}
        right={(
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        )}
      />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6">
          <aside className="rounded-2xl border border-zinc-800 bg-zinc-900 p-3 h-fit">
            <div className="px-3 py-2">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Platform</p>
            </div>
            <nav className="space-y-1">
              {navItems.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20'
                        : 'text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 border border-transparent'
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>
          </aside>

          <main className="space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-zinc-100">{title}</h1>
                <p className="text-sm text-zinc-500 mt-1">{description}</p>
              </div>
              {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
            </div>
            {children}
          </main>
        </div>
      </div>
    </AppLayout>
  );
}
